import React, { useContext, useState } from 'react'
import { useEffect } from 'react';
import { PeFilterContext } from '../../Contexts/PeFilterContext'
import Popup from '../Common/Popup'

/**
 * The AcquirerDealRangeFilter function is a React component that renders a filter for selecting a deal
 * range in US$ M, with minimum and maximum options.
 * @returns The AcquirerDealRangeFilter component is returning a JSX element that renders a filter for
 * selecting a deal range. It includes two select dropdowns for selecting the minimum and maximum deal
 * range values, and also displays the selected values.
 */
function AcquirerDealRangeFilter({ MinDealOptions,
    MaxDealOptions,
    setMinDealOptions,
    setMaxDealOptions,
    customPathname,
    label = "Deal Range (US$ M)",
    MinDealRange = [],
    MaxDealrange = [],
    dataPath="dealRange",
    isPopupOpen,
    setIsPopupOpen
}) {
    const { refineSearchFilters } = useContext(PeFilterContext);
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;
    const [error, setError] = useState("");

    useEffect(() => {
        const min = refineSearchFilters[customPathname]?.[dataPath]?.at(0)
        const max = refineSearchFilters[customPathname]?.[dataPath]?.at(1)
        setMinDealOptions(MinDealRange?.map(option => ({ ...option, isChecked: min?.name == option.name })))
        setMaxDealOptions(MaxDealrange?.map(option => ({ ...option, isChecked: max?.name == option.name })))
    }, [refineSearchFilters, customPathname, setMinDealOptions, setMaxDealOptions, MinDealRange, MaxDealrange,dataPath])

    const selectedMin = MinDealOptions?.filter(option => option.isChecked)?.at(0)
    const selectedMax = MaxDealOptions?.filter(option => option.isChecked)?.at(0)


  /**
   * The function `handleMinRange` checks if the selected minimum range is greater than the selected
   * maximum range and updates the minimum deal options accordingly.
   * @param e - The parameter `e` is an event object that is passed to the `handleMinRange` function.
   * It represents the event that triggered the function, such as a change event on an input element.
   */
    const handleMinRange = (e) => {
        const { value } = e.target;
        if(selectedMax){
            if (parseInt(value) > parseInt(selectedMax?.name ?? 0)) {
                setError('Min deal range cannot be greater than max deal range');
                return;
            }
        }
        setMinDealOptions(MinDealOptions?.map(type => ({ ...type, isChecked: value === "" ? false : type.name === value })));
        setError("");
    }

    const handleMaxRange = (e) => {
        const { value } = e.target;
        if (selectedMin && parseInt(value) < parseInt(selectedMin?.name ?? 0)) {
            setError('Max deal range cannot be less than min deal range');
            return;
        }
        setMaxDealOptions(MaxDealOptions?.map(type => ({ ...type, isChecked: value === "" ? false : type.name === value })));
        setError("");
    }

    const getDisplayText = () => {
        if (selectedMin && selectedMax) {
            return `: ${selectedMin.name} to ${selectedMax.name}`;
        } else if (selectedMin) {
            return `: From ${selectedMin.name}`;
        } else if (selectedMax) {
            return `: Up to ${selectedMax.name}`;
        }
        return " ";
    };

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100  text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">{label}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {(selectedMin || selectedMax) && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {getDisplayText()}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)}>
                    {/* <div className="p-4 min-w-[300px]"> */}
                    <div className="p-4 min-w-[225px] md:min-w-[300px]">
                        <div className="space-y-3">
                            <div className="mb-3">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    From (US$ M)
                                </label>
                                <select 
                                    value={selectedMin?.name ?? ""} 
                                    onChange={handleMinRange}
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                                        error && error.includes('Min')
                                            ? 'border-red-500 focus:ring-2 focus:ring-red-500'
                                            : 'border-gray-300 focus:ring-[#BC8B20]'
                                    }`}
                                >
                                    <option value="">All</option>
                                    {MinDealOptions && MinDealOptions.length > 0 ? (
                                        MinDealOptions.map((option, index) => (
                                            <option key={index} value={option.name ? option.name : ''}>
                                                {option.name ? option.name : ''}
                                            </option>
                                        ))
                                    ) : (
                                        <option disabled>No results found!</option>
                                    )}
                                </select>
                            </div>
                            
                            <div className="mb-3">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    To (US$ M)
                                </label>
                                <select 
                                    value={selectedMax?.name ?? ""} 
                                    onChange={handleMaxRange}
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                                        error && error.includes('Max')
                                            ? 'border-red-500 focus:ring-2 focus:ring-red-500'
                                            : 'border-gray-300 focus:ring-[#BC8B20]'
                                    }`}
                                >
                                    <option value="">All</option>
                                    {MaxDealOptions && MaxDealOptions.length > 0 ? (
                                        MaxDealOptions.map((option, index) => (
                                            <option key={index} value={option.name ? option.name : ''}>
                                                {option.name ? option.name : ''}
                                            </option>
                                        ))
                                    ) : (
                                        <option disabled>No results found!</option>
                                    )}
                                </select>
                            </div>
                            
                            {error && (
                                <div className="text-red-600 text-xs font-medium">
                                    {error}
                                </div>
                            )}
                        </div>
                    </div>
                </Popup>
            )}
        </div>
    )
}

export default AcquirerDealRangeFilter